import { Form, Formik, FormikValues } from "formik";
import { Button, Card, Input, Select } from "../../custom";
import styles from "./style.module.scss";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { App, Tag } from "antd";
import {
  getPaymentChannels,
  queryProcessedPaymentWithChannel,
} from "../../utils/request";
import { routes } from "../../routes";
import { formatFullDate, formattedTime } from "../../utils/formatDate";

const PAYMENT_SUCCESSFUL = ["Successful", "200", "00"];
const PAYMENT_FAILED = "Failed";

const validationSchema = Yup.object().shape({
  transactionReference: Yup.string().required(
    "Transaction reference is required",
  ),
  paymentChannel: Yup.string().required("Payment channel is required"),
});

const RequeryPayment = () => {
  const { notification } = App.useApp();

  const paymentChannelsQuery = useQuery({
    queryKey: ["paymentChannels"],
    queryFn: getPaymentChannels,
  });
  const fetchedChannels = paymentChannelsQuery.data?.data as
    | PaymentChannel[]
    | undefined;
  // Cyberpay is currently the only active channel; fall back to it if the
  // channel list API returns nothing so applicants aren't blocked.
  const paymentChannels =
    fetchedChannels && fetchedChannels.length > 0
      ? fetchedChannels
      : [{ name: "Remita" } as PaymentChannel];

  const requeryMutation = useMutation({
    mutationFn: queryProcessedPaymentWithChannel,
  });
  const result = requeryMutation.data as PaymentConfirmation | undefined;

  const handleRequeryPayment = async (values: FormikValues) => {
    try {
      await requeryMutation.mutateAsync({
        reference: values.transactionReference,
        channel: values.paymentChannel,
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message ||
          "Unable to retrieve transaction status.",
      });
    }
  };

  return (
    <section className={styles.container}>
      <div>
        <Card>
          <div className={styles.cardChild}>
            <div>
              <h3>Requery Pending Payment</h3>
              <p>
                Enter your transaction reference and the payment channel you
                used to check the status of your payment.
              </p>
            </div>

            <div>
              <div>
                <Formik
                  initialValues={{
                    transactionReference: "",
                    paymentChannel: "Remita",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleRequeryPayment}
                >
                  {() => (
                    <Form>
                      <div className={styles.formStyle}>
                        <Input
                          name="transactionReference"
                          label="Transaction Reference"
                          placeholder="Enter your transaction reference"
                          asterisk
                        />
                        <Select
                          name="paymentChannel"
                          label="Payment Channel"
                          placeholder="Select the channel you paid with"
                          disabled={paymentChannelsQuery.isLoading}
                          asterisk
                          options={paymentChannels?.map((channel) => (
                            <option key={channel.name} value={channel.name}>
                              {channel.name}
                            </option>
                          ))}
                        />
                      </div>

                      <div>
                        <div>
                          <Button
                            text="CHECK STATUS"
                            type="submit"
                            disabled={requeryMutation.isPending}
                            isLoading={requeryMutation.isPending}
                          />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>

            {result && (
              <div className={styles.resultCard}>
                <div className={styles.resultRow}>
                  <span>Status</span>
                  <Tag
                    color={
                      PAYMENT_SUCCESSFUL.includes(result.transactionStatus)
                        ? "green"
                        : result.transactionStatus === PAYMENT_FAILED
                          ? "red"
                          : "gold"
                    }
                  >
                    {result.transactionStatus}
                  </Tag>
                </div>
                <div className={styles.resultRow}>
                  <span>Reference</span>
                  <b>{result.transactionReference}</b>
                </div>
                {result.transactionDate && (
                  <div className={styles.resultRow}>
                    <span>Date</span>
                    <b>
                      {formatFullDate(result.transactionDate)},{" "}
                      {formattedTime(result.transactionDate)}
                    </b>
                  </div>
                )}
                <p className={styles.resultMessage}>{result.message}</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <Link to={routes.auth.landing}>
              <p>Back to Home</p>
            </Link>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default RequeryPayment;
